import type { ConfirmEmailResponse } from 'types'

import { useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import styled, { keyframes } from 'styled-components'

import { clientAccessBaseUrl, useStore } from 'store'
import copy from 'copy.json'

import Button from 'components/Button'
import { CheckBigIcon, CrossBigIcon } from 'icons'

export default function SuccessScreen() {
  const { api } = useStore()
  const { id } = useParams()
  const location = useLocation()
  const [response, setResponse] = useState<ConfirmEmailResponse>()
  const [counter, setCounter] = useState(5)

  const token = new URLSearchParams(location.search).get('setPasswordToken')
  const passwordPageUrl = `${clientAccessBaseUrl}/setPassword?token=${token}`

  useEffect(() => {
    if (response && response.code !== 200) return

    const timer = setTimeout(() => counter > 0 && setCounter(counter - 1), 1000)

    if (counter === 0 && response?.code === 200) {
      clearTimeout(timer)
      window.location.href = passwordPageUrl
    }

    return () => clearTimeout(timer)
  }, [counter, passwordPageUrl, response])

  useEffect(() => {
    api.confirmEmail(id).then(setResponse)
  }, [api, id])

  const success = (
    <Wrapper className="dark">
      <CheckBigIcon />
      <h1>{copy.page.email_verification.heading}</h1>
      <Description>
        <p>{copy.page.email_verification.paragraph_first}</p>
        <p>
          {copy.page.email_verification.paragraph_second_1}
          <strong> {counter} </strong>
          {copy.page.email_verification.paragraph_second_2}
        </p>
      </Description>

      <Button onClick={() => (window.location.href = passwordPageUrl)} secondary>
        {copy.page.email_verification.button}
      </Button>
    </Wrapper>
  )

  const error = (
    <Wrapper className="dark" background="var(--orange)">
      <CrossBigIcon />
      <h1>{copy.page.email_verification.heading_error}</h1>
      <p>{copy.page.email_verification.paragraph_error}</p>

      <p>
        <strong>{copy.page.email_verification.paragraph_error_strong}</strong>
      </p>

      <Contact>
        <a href={`mailto:${copy.common.company_mail}`}>{copy.common.company_mail}</a>
        <span>{copy.common.or}</span>
        <a href={`tel:${copy.common.company_phone}`}>{copy.common.company_phone}</a>
      </Contact>
    </Wrapper>
  )

  return !response ? null : response.code === 200 ? success : error
}

const fadeIn = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`

const Wrapper = styled.div<{ background?: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;

  width: 100%;
  max-width: 646px;
  margin: 50px auto 0;
  padding: 48px clamp(48px, 20vw, 130px);

  color: var(--white);
  font: var(--400-15);
  text-align: center;

  background: ${({ background }) => background};
  border-radius: 4px;
  animation: ${fadeIn} 0.3s ease-in-out;

  h1 {
    font: var(--600-24);
  }

  strong {
    font: var(--600-16);
  }

  a {
    color: inherit;
  }

  button {
    width: 100%;
    max-width: 312px;

    &:hover:enabled:not(:focus-visible) {
      background: var(--very-light-green);
    }
  }
`

Wrapper.defaultProps = {
  background: 'var(--green)'
}

const Contact = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`

const Description = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`
